import { useRef, useState } from 'react';
import { IconFileTypePdf, IconUpload } from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';

/** The paste shortcut as this platform writes it. */
const PASTE_KEYS = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
    ? '⌘ V'
    : 'Ctrl + V';

/**
 * What the editor shows before it has a document: a card in the middle of the
 * canvas area that takes a PDF by click, drop or paste. The whole area is the drop
 * target, so a file let go anywhere over the empty editor still lands.
 */
export default function UploadPrompt({ onFiles }) {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);

    const browse = () => inputRef.current?.click();

    return (
        <div
            className="flex flex-1 items-center justify-center overflow-auto p-4"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={(e) => {
                // Only when the pointer really leaves the area, not when it
                // crosses onto one of the card's children.
                if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false);
            }}
            onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                onFiles(e.dataTransfer.files);
            }}
        >
            <div className="w-full max-w-[460px] rounded-2xl border border-[#ECE8E4] bg-white p-5 shadow-[0_12px_40px_rgba(28,24,21,0.10)] dark:border-zinc-800 dark:bg-zinc-900">
                <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-[#FDECE7] text-[#D33A2C] dark:bg-orange-950/40 dark:text-orange-400">
                        <IconFileTypePdf size={22} stroke={1.7} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[15px] font-semibold text-[#1C1815] dark:text-zinc-100">
                            {t('pdfEditor.upload.heading')}
                        </div>
                        <div className="text-[12.5px] text-[#6B635C] dark:text-zinc-400">
                            {t('pdfEditor.upload.sub')}
                        </div>
                    </div>
                </div>

                <div
                    role="button"
                    tabIndex={0}
                    onClick={browse}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && browse()}
                    className={`flex w-full cursor-pointer flex-col items-center gap-2 rounded-[14px] border-[1.5px] border-dashed px-4 py-10 transition-colors ${
                        dragging
                            ? 'border-primary bg-primary/5'
                            : 'border-[#E9A08F] bg-[#FDF3F0] hover:border-[#c93d22] dark:border-orange-700 dark:bg-orange-950/20'
                    }`}
                >
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-[#D33A2C] shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-800 dark:text-orange-400">
                        <IconUpload size={21} stroke={1.8} />
                    </div>
                    <div className="text-center text-sm font-semibold text-[#1C1815] dark:text-zinc-100">
                        {t('pdfEditor.upload.title')}
                    </div>
                    <div className="text-xs text-[#A8A099] dark:text-zinc-500">
                        {t('pdfEditor.upload.hint')}
                    </div>
                </div>

                {/* A PDF copied in Finder / Explorer pastes straight in; the page
                    listens for it (EditPdf), this just says so. */}
                <div className="mt-3 flex items-center justify-center gap-1.5 text-[12px] text-[#6B635C] dark:text-zinc-400">
                    <span>{t('pdfEditor.upload.pasteBefore')}</span>
                    <kbd className="rounded-md border border-[#E2DCD6] bg-[#F7F5F2] px-1.5 py-0.5 font-sans text-[11px] font-semibold text-[#3D3833] shadow-[0_1px_0_#E2DCD6] dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200">
                        {PASTE_KEYS}
                    </kbd>
                    <span>{t('pdfEditor.upload.pasteAfter')}</span>
                </div>

                <input
                    ref={inputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }}
                />
            </div>
        </div>
    );
}
